import { Controller } from "@hotwired/stimulus"

// Shown after Stripe Checkout while the webhook activates the subscription.
// Polls a status endpoint every ~2s and redirects once active. A ~20s
// slow-notice link prevents the page from hanging visually.
//
// The webhook is the fast path and the poll sees it as soon as it lands. When
// one is late or dropped, the page would otherwise wait forever, so past a
// threshold the poll starts asking the server to reconcile straight from Stripe.
// That ask is rate-limited to one every reconcileEveryMs so a delayed webhook
// costs a handful of Stripe calls, not one every two seconds.
export default class extends Controller {
  static values = {
    statusUrl: String,
    fallbackUrl: String,
    intervalMs: { type: Number, default: 2000 },
    reconcileAfterMs: { type: Number, default: 10000 },
    reconcileEveryMs: { type: Number, default: 10000 }
  }
  static targets = ["slow"]

  connect() {
    this.stopped = false
    this.startedAt = Date.now()
    this.lastReconcileAt = 0
    this.poll()
    this.slowTimer = setTimeout(() => {
      if (this.hasSlowTarget) this.slowTarget.classList.remove("hidden")
    }, 20000)
  }

  disconnect() {
    this.stopped = true
    if (this.pollTimer) clearTimeout(this.pollTimer)
    if (this.slowTimer) clearTimeout(this.slowTimer)
  }

  poll() {
    if (this.stopped) return

    const url = new URL(this.statusUrlValue, window.location.origin)
    if (this.shouldReconcile()) {
      url.searchParams.set("reconcile", "1")
      this.lastReconcileAt = Date.now()
    }

    fetch(url, { headers: { Accept: "application/json" } })
      .then((r) => r.json())
      .then((data) => {
        if (data.activated) {
          window.location.href = data.redirect_url || this.fallbackUrlValue
        } else {
          this.schedule()
        }
      })
      .catch(() => this.schedule())
  }

  // Only once the webhook has had its chance, and then no more often than
  // reconcileEveryMs, so the common case (webhook lands in a second or two)
  // never spends a Stripe call.
  shouldReconcile() {
    if (Date.now() - this.startedAt < this.reconcileAfterMsValue) return false
    return Date.now() - this.lastReconcileAt >= this.reconcileEveryMsValue
  }

  schedule() {
    if (this.stopped) return
    this.pollTimer = setTimeout(() => this.poll(), this.intervalMsValue)
  }
}
